import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { LOGO_URL } from '../../mock/mock';
import { useSettings } from '../../context/SettingsContext';
import { Facebook, Instagram, Twitter, Youtube, Mail, Phone } from 'lucide-react';

import { API } from '../../config';

const Footer = () => {
  const settings = useSettings();
  const g = settings?.general || {};
  const mods = settings?.modules || {};
  const logoSrc = g.logo_url || LOGO_URL;
  const siteName = g.site_name || 'TrainPanda';
  const supportPhone = g.support_phone || '1800-123-4567';
  const supportEmail = g.support_email || 'help@trainpanda.com';

  // Dynamic CMS pages (Privacy, Terms, plus any custom pages admin creates)
  const [pages, setPages] = useState([]);
  useEffect(() => {
    axios.get(`${API}/pages`).then(r => setPages(r.data?.pages || [])).catch(() => {});
  }, []);

  // Promote known legal slugs to the top
  const order = { 'privacy-policy': 1, 'terms-of-use': 2, 'terms': 2, 'refund-policy': 3, 'about': 4 };
  const sorted = [...pages].sort((a, b) => (order[a.slug] || 99) - (order[b.slug] || 99));

  return (
    <footer className="bg-gradient-midnight text-white/80 mt-16">
      <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2 md:col-span-1">
          <img src={logoSrc} alt={siteName} className="h-12" />
          <p className="text-sm mt-3 text-white/60 leading-relaxed">
            {g.tagline || "India's trusted companion for train travel — live running status, PNR check and hot meals at your seat."}
          </p>
          <div className="flex gap-3 mt-4">
            <Facebook size={18} className="cursor-pointer hover:text-tp-coral" />
            <Instagram size={18} className="cursor-pointer hover:text-tp-coral" />
            <Twitter size={18} className="cursor-pointer hover:text-tp-coral" />
            <Youtube size={18} className="cursor-pointer hover:text-tp-coral" />
          </div>
        </div>

        <div>
          <h4 className="text-white font-bold mb-3">Services</h4>
          <ul className="space-y-2 text-sm">
            {mods.live_status !== false && <li><Link to="/live-train-status" className="hover:text-tp-coral">Live Train Status</Link></li>}
            {mods.pnr_status !== false && <li><Link to="/pnr-status" className="hover:text-tp-coral">PNR Status</Link></li>}
            {mods.food_on_train !== false && <li><Link to="/food-on-train" className="hover:text-tp-coral">Food on Train</Link></li>}
            {mods.stories !== false && <li><Link to="/stories" className="hover:text-tp-coral">Travel Stories</Link></li>}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold mb-3">Legal</h4>
          <ul className="space-y-2 text-sm">
            {sorted.length === 0 ? (
              <>
                <li><Link to="/page/privacy-policy" className="hover:text-tp-coral">Privacy Policy</Link></li>
                <li><Link to="/page/terms-of-use" className="hover:text-tp-coral">Terms of Use</Link></li>
              </>
            ) : (
              sorted.map(p => (
                <li key={p.slug}>
                  <Link to={`/page/${p.slug}`} data-testid={`footer-page-${p.slug}`} className="hover:text-tp-coral">{p.title}</Link>
                </li>
              ))
            )}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold mb-3">Support</h4>
          <div className="text-sm space-y-2">
            <a href={`tel:${supportPhone}`} className="flex items-center gap-2 hover:text-tp-coral"><Phone size={14} className="text-tp-coral" /> {supportPhone}</a>
            <a href={`mailto:${supportEmail}`} className="flex items-center gap-2 hover:text-tp-coral"><Mail size={14} className="text-tp-coral" /> {supportEmail}</a>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-4 text-center text-xs text-white/50">
        © {new Date().getFullYear()} {siteName}. All rights reserved.
      </div>
    </footer>
  );
};

export default Footer;
